import { useEffect, useState } from 'react';
import { OVERLAY_PRIO, useOverlaySlot } from '../lib/overlays';
import { X, Share, MoreHorizontal, Plus, ExternalLink, Globe, Compass, ChevronLeft } from 'lucide-react';
import './PwaBanner.css';

const SITE_URL = 'https://heads-up-poker.ru';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

function isTelegramWebApp() {
  return !!(window as any).Telegram?.WebApp?.initData;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

const DISMISSED_KEY = 'pwa_banner_dismissed';

export default function PwaBanner() {
  // Remembered for the session — it used to come back on every reload.
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [showModal, setShowModal] = useState(false);
  // Don't greet people with it: let the screen settle first.
  const [ready, setReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setReady(true), 4000); return () => clearTimeout(t); }, []);

  const visible = useOverlaySlot('pwa-banner', ready && !dismissed && !isStandalone(), OVERLAY_PRIO.pwaBanner, false);
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  };

  if (!visible && !showModal) return null;

  const inTelegram = isTelegramWebApp();
  const ios = isIOS();

  return (
    <>
      {/* Bottom banner */}
      {visible && <div className="pwa-banner">
        <div className="pwa-banner__main">
          <button className="pwa-banner__close" onClick={dismiss}>
            <X size={16} />
          </button>
          <img className="pwa-banner__icon" src="/pwa-192x192.png" alt="" />
          <div className="pwa-banner__text">
            <span className="pwa-banner__title">Установите приложение</span>
            <span className="pwa-banner__subtitle">Быстрый доступ и мгновенная загрузка</span>
          </div>
          <button className="pwa-banner__how" onClick={() => setShowModal(true)}>
            Как установить?
          </button>
        </div>
      </div>}

      {/* Full-screen modal with instructions */}
      {showModal && (
        <div className="pwa-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pwa-modal" onClick={e => e.stopPropagation()}>
            <button className="pwa-modal__close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>

            <img className="pwa-modal__icon" src="/pwa-192x192.png" alt="" />
            <div className="pwa-modal__app-name">Heads Up</div>
            <div className="pwa-modal__app-desc">Покерный клуб</div>

            <div className="pwa-modal__description">
              Этот веб-сайт имеет функционал приложения. Добавьте его на экран «Домой» — это откроет дополнительные возможности и сделает использование удобнее.
            </div>

            <div className="pwa-modal__divider" />

            <div className="pwa-modal__steps">
              {/* Step 1: open in correct browser */}
              <div className="pwa-modal__step">
                <span className="pwa-modal__step-icon"><Compass size={20} /></span>
                <span>
                  Откройте сайт в браузере <b>{ios ? 'Safari' : 'Chrome'}</b> (не в Telegram)
                </span>
              </div>
              {inTelegram && (
                <a className="pwa-modal__link" href={SITE_URL} target="_blank" rel="noopener noreferrer">
                  <Globe size={14} />
                  {SITE_URL.replace('https://', '')}
                  <ExternalLink size={12} />
                </a>
              )}

              {ios ? (
                <>
                  <div className="pwa-modal__step">
                    <span className="pwa-modal__step-icon"><MoreHorizontal size={20} /></span>
                    <span>Нажмите <b>"Ещё"</b>, если нет значка <b>"Поделиться"</b></span>
                  </div>
                  <div className="pwa-modal__step">
                    <span className="pwa-modal__step-icon"><Share size={20} /></span>
                    <span>Нажмите <b>"Поделиться"</b></span>
                  </div>
                  <div className="pwa-modal__step">
                    <span className="pwa-modal__step-icon"><Plus size={20} /></span>
                    <span>Прокрутите вниз и нажмите <b>"На экран «Домой»"</b></span>
                  </div>
                </>
              ) : (
                <>
                  <div className="pwa-modal__step">
                    <span className="pwa-modal__step-icon"><MoreHorizontal size={20} /></span>
                    <span>Нажмите <b>⋮</b> в правом верхнем углу браузера</span>
                  </div>
                  <div className="pwa-modal__step">
                    <span className="pwa-modal__step-icon"><Plus size={20} /></span>
                    <span>Выберите <b>"Установить приложение"</b> или <b>"Добавить на главный экран"</b></span>
                  </div>
                </>
              )}
            </div>

            <button className="pwa-modal__back" onClick={() => setShowModal(false)}>
              <ChevronLeft size={18} />
              Назад
            </button>
          </div>
        </div>
      )}
    </>
  );
}
